<!--
  Borderò — list of generated delivery receipts signed by couriers.
  Redesigned 2026-06-12 (v2.7.0 path, Phase 1.5 Chunk C.1).

  Drops kendo-grid (1) + kendo-datepicker × 2 + kendo-dropdownlist
  (1) + mat-spinner (1) + ~40 inline `style="..."` attributes.

  Pattern matches the supplier / agent / inventory families:
  sticky header, filter bar (native date range + native select +
  search), Diakonix table-card, skeleton loader.

  Click row → opens <app-borderò-dettagli> modal preserved as-is
  (Chunk C.2 will restyle the dettagli template).
-->

<div class="bordero-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="bordero-header">
    <div class="bordero-header__titles">
      <h1 class="bordero-header__title">📑 Borderò</h1>
      <p class="bordero-header__subtitle">
        @if (filteredBordero.length > 0) {
          <strong>{{ (filteredBordero | byBordero:[filters.startingDate, filters.endDate, filters.dateOrginizedBy.value]).length }}</strong>
          borderò visibili
          @if (chosenCarrier) { · corriere <strong>{{ chosenCarrier }}</strong> }
          @if (filterTerm) { · cliente <strong>"{{ filterTerm }}"</strong> }
        } @else if (loading) {
          Caricamento borderò…
        } @else {
          Nessun borderò generato
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="refreshFunction()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >🔁</button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="bordero-filters" aria-label="Filtri ricerca">

    <!-- Date range (signature date) -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data firma corriere</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <!-- Courier filter -->
    <div class="filter-field">
      <label class="filter-field__label" for="filterCarrier">Corriere</label>
      <select
        id="filterCarrier"
        class="filter-field__input filter-field__input--select"
        [(ngModel)]="chosenCarrier"
        (ngModelChange)="changeCourier($event)"
      >
        @for (carrier of availableCarriers; track carrier) {
          <option [ngValue]="carrier === showAll ? null : carrier">
            {{ carrier || '— non specificato —' }}
          </option>
        }
      </select>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="bordero-table-card" aria-label="Elenco borderò">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredBordero.length === 0) {
      <div class="bordero-empty">
        <div class="bordero-empty__icon" aria-hidden="true">
          @if (filterTerm || chosenCarrier) { 🔎 } @else { 📭 }
        </div>
        <p class="bordero-empty__msg">
          @if (filterTerm || chosenCarrier) {
            Nessun borderò corrisponde ai filtri attivi.
          } @else {
            Nessun borderò generato al momento.
          }
        </p>
      </div>
    } @else {
      <div class="bordero-table-scroll">
        <table class="bordero-table" role="table">
          <thead>
            <tr>
              <th class="col-num is-sortable" (click)="sortBy('order_number')">
                N° spedizione
                @if (sortColumn === 'order_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('bo_data')">
                Data firma
                @if (sortColumn === 'bo_data') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-num is-sortable" (click)="sortBy('box_number')">
                N° colli
                @if (sortColumn === 'box_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-carrier is-sortable" (click)="sortBy('bo_courier_name')">
                Corriere
                @if (sortColumn === 'bo_courier_name') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of filteredBordero
                | byBordero: [filters.startingDate, filters.endDate, filters.dateOrginizedBy.value];
              track item.bordero_id + '-' + ($index)
            ) {
              <tr
                class="bordero-row"
                (click)="gotoDetails(item.bordero_id)"
                title="Apri dettagli borderò"
              >
                <td class="col-num">
                  <span class="cell-num">{{ item.order_number ?? 0 }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.bo_data ? (item.bo_data | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-num">
                  <span class="cell-num">{{ item.box_number ?? 0 }}</span>
                </td>
                <td class="col-carrier">
                  <span class="cell-carrier">
                    {{ item.bo_courier_name || '—' }}
                  </span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  @if (item.bo_pdf_link) {
                    <a
                      class="icon-btn icon-btn--pdf"
                      [href]="item.bo_pdf_link"
                      target="_blank"
                      rel="noopener"
                      title="Apri PDF borderò"
                      aria-label="Apri PDF borderò"
                    >📄</a>
                  } @else {
                    <span class="icon-btn icon-btn--disabled" title="PDF non disponibile">—</span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ─── Bordero dettagli modal (preserved, Chunk C.2 restyle) ──── -->
@if (openModal.isBordero) {
  <app-borderò-dettagli
    [opened]="openModal.isBordero"
    [order_id]="clickedOrder"
  ></app-borderò-dettagli>
}
